import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import {
  antecipacaoDtoToAntecipacao,
  antecipacaoToSalvarCommand,
  beneficioDtoToOpcao,
  beneficioToSalvarCommand,
  elegibilidadeDtoToBeneficio,
  elegibilidadesDtoToElegibilidadeBeneficios,
  opcoesRendaDtoToOpcoesRenda,
  opcoesRendaToSalvarCommand,
  regraValorMinimoSaldoDtoToRegra,
  regraValorMinimoSaldoToSalvarCommand,
  rentabilidadeDtoToRentabilidade,
  rentabilidadeToSalvarCommand,
} from '../../mappers/parametros/beneficios-api.mapper';
import {
  Antecipacao,
  BeneficioElegibilidade,
  BeneficioOpcao,
  ElegibilidadeBeneficios,
  OpcoesRenda,
  RegraValorMinimoSaldo,
  Rentabilidade,
} from '../../models/parametros/beneficios.model';
import { BeneficiosApiService } from './beneficios-api.service';
import { ExtratoDesligamentoApiService } from './extrato-desligamento-api.service';

/** Só os registros ativos (catálogos) ou ainda no plano (fora da lixeira). */
const SO_ATIVOS = { ativo: 'S' };

/**
 * Elegibilidade, opções de renda, antecipação, rentabilidade e regra de valor mínimo do saldo vêm da
 * API (`/v1/planos-param/{id}/...`).
 */
@Injectable({
  providedIn: 'root',
})
export class BeneficiosService {
  private readonly apiService = inject(BeneficiosApiService);
  private readonly extratoApiService = inject(ExtratoDesligamentoApiService);

  /** Benefícios do plano (sem os da lixeira), cada um com as regras dele, e os catálogos das regras. */
  obterElegibilidade(planoId: number): Observable<ElegibilidadeBeneficios> {
    return forkJoin({
      beneficios: this.apiService.listarBeneficiosPlano(planoId, SO_ATIVOS),
      situacoes: this.extratoApiService.listarSituacoesCadastrais(SO_ATIVOS),
      comprovacoes: this.apiService.listarComprovacoes(SO_ATIVOS),
    }).pipe(
      switchMap(({ beneficios, situacoes, comprovacoes }) =>
        (beneficios.length
          ? forkJoin(beneficios.map((item) => this.apiService.obterElegibilidade(planoId, item.id_beneficio)))
          : of([])
        ).pipe(map((elegibilidades) => elegibilidadesDtoToElegibilidadeBeneficios(elegibilidades, situacoes, comprovacoes))),
      ),
    );
  }

  /** Salvar de um benefício: cria ou reativa a linha no plano e substitui as regras. Devolve o gravado. */
  salvarBeneficio(planoId: number, beneficio: BeneficioElegibilidade): Observable<BeneficioElegibilidade> {
    return this.apiService
      .salvarElegibilidade(planoId, beneficio.id, beneficioToSalvarCommand(beneficio))
      .pipe(map(elegibilidadeDtoToBeneficio));
  }

  /** Toggle "Ativo no autoatendimento" de um benefício já salvo. */
  alterarAutoatendimento(planoId: number, idBeneficio: number, ativo: boolean): Observable<void> {
    return this.apiService.alterarAutoatendimento(planoId, idBeneficio, ativo).pipe(map(() => undefined));
  }

  /** Lixeira: tira o benefício do plano; a API guarda as regras para quando ele voltar. */
  removerDoPlano(planoId: number, idBeneficio: number): Observable<void> {
    return this.apiService.alterarStatusNoPlano(planoId, idBeneficio, false).pipe(map(() => undefined));
  }

  obterOpcoesRenda(planoId: number): Observable<OpcoesRenda> {
    return this.apiService.obterOpcoesRenda(planoId).pipe(map(opcoesRendaDtoToOpcoesRenda));
  }

  salvarOpcoesRenda(planoId: number, opcoes: OpcoesRenda): Observable<OpcoesRenda> {
    return this.apiService
      .salvarOpcoesRenda(planoId, opcoesRendaToSalvarCommand(opcoes))
      .pipe(map(opcoesRendaDtoToOpcoesRenda));
  }

  obterAntecipacao(planoId: number): Observable<Antecipacao> {
    return this.apiService.obterAntecipacao(planoId).pipe(map(antecipacaoDtoToAntecipacao));
  }

  salvarAntecipacao(planoId: number, antecipacao: Antecipacao): Observable<Antecipacao> {
    return this.apiService
      .salvarAntecipacao(planoId, antecipacaoToSalvarCommand(antecipacao))
      .pipe(map(antecipacaoDtoToAntecipacao));
  }

  obterRentabilidade(planoId: number): Observable<Rentabilidade> {
    return this.apiService.obterRentabilidade(planoId).pipe(map(rentabilidadeDtoToRentabilidade));
  }

  salvarRentabilidade(planoId: number, rentabilidade: Rentabilidade): Observable<Rentabilidade> {
    return this.apiService
      .salvarRentabilidade(planoId, rentabilidadeToSalvarCommand(rentabilidade))
      .pipe(map(rentabilidadeDtoToRentabilidade));
  }

  obterRegraValorMinimoSaldo(planoId: number): Observable<RegraValorMinimoSaldo> {
    return this.apiService.obterValorMinimoSaldo(planoId).pipe(map(regraValorMinimoSaldoDtoToRegra));
  }

  salvarRegraValorMinimoSaldo(planoId: number, regra: RegraValorMinimoSaldo): Observable<RegraValorMinimoSaldo> {
    return this.apiService
      .salvarValorMinimoSaldo(planoId, regraValorMinimoSaldoToSalvarCommand(regra))
      .pipe(map(regraValorMinimoSaldoDtoToRegra));
  }

  /** Catálogo de benefícios ativos, que podem entrar no plano. */
  listarBeneficiosDisponiveis(): Observable<BeneficioOpcao[]> {
    return this.apiService.listarCatalogo(SO_ATIVOS).pipe(map((beneficios) => beneficios.map(beneficioDtoToOpcao)));
  }
}
